import React, { useMemo, useState } from "react";
import { MapPin, Ticket } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";
import VoixPassport from "@/components/VoixPassport";

export default function ListeningStats() {
  const { stats, hours } = usePlayer();
  const { t } = useI18n();
  const [showPassport, setShowPassport] = useState(false);

  const items = useMemo(() => {
    if (!stats || !stats.counts) return [];
    return Object.entries(stats.counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6);
  }, [stats]);

  const total = items.length;
  const max = items[0]?.[1] || 1;
  const hoursDisplay = useMemo(() => {
    const s = hours?.seconds || 0;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${String(m).padStart(2, "0")}m`;
  }, [hours]);

  return (
    <>
      <div data-testid="listening-stats">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-green-400">{t("stats.pre")}</div>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mt-1">
              {t("stats.title")} <span className="text-green-400">{total}</span> {t("stats.countries")}
            </h2>
            {(hours?.seconds || 0) > 0 && (
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                {hoursDisplay} {t("passport.hours_of_radio")}
              </div>
            )}
          </div>
          <button
            data-testid="open-passport"
            onClick={() => setShowPassport(true)}
            className="flex items-center gap-2 border border-cyan-500 text-cyan-300 px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest hover:bg-cyan-500/10 transition"
          >
            <Ticket size={12} />
            <span>{t("passport.open")}</span>
          </button>
        </div>

        {total === 0 ? (
          <p className="font-body text-sm text-slate-500 mt-2 max-w-lg">{t("stats.no_data")}</p>
        ) : (
          <div className="mt-5 space-y-2.5 max-w-2xl">
            {items.map(([country, count], idx) => {
              const pct = Math.max(6, (count / max) * 100);
              return (
                <div key={country} data-testid={`stats-row-${country}`} className="flex items-center gap-3">
                  <div className="w-6 font-mono-tech text-[10px] text-slate-600">#{idx + 1}</div>
                  <div className="w-40 shrink-0 flex items-center gap-2">
                    <MapPin size={11} className="text-green-400 shrink-0" />
                    <span className="font-mono-tech text-[11px] uppercase tracking-widest text-slate-300 truncate">{country}</span>
                  </div>
                  <div className="flex-1 h-2 bg-white/5 relative">
                    <div className="h-full bg-gradient-to-r from-green-500/70 to-cyan-500/70" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-10 text-right font-mono-tech text-[11px] text-slate-400">{count}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showPassport && <VoixPassport onClose={() => setShowPassport(false)} />}
    </>
  );
}
